/**
 * API dashboard BI margine (R6, 2026-07-03).
 *
 * Legge gli aggregati materializzati (`economics.order_margins`): il margine
 * arriva SOLO dagli ordini calcolabili, con copertura esplicita — la UI deve
 * sempre mostrare quanti ordini sono stime (≈) o non calcolabili.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPost, prettifyApiError } from "@/shared/api";

export interface BiTotals {
  orders: number;
  revenue: number;
  margin: number;
  revenue_computable: number;
  computable_count: number;
  estimate_count: number;
  not_computable_count: number;
  /** Sostituzioni nel periodo (Davide 2026-07-03): conteggio + costo NETTO
   *  malgrado il nome del campo (replacements_summary). */
  replacements_count: number;
  replacements_cost_gross: number;
}

export interface BiDayRow {
  day: string; // ISO yyyy-mm-dd (giorno business Europe/Rome)
  orders: number;
  revenue: number;
  margin: number;
  computable_count: number;
}

export interface BiSkuRow {
  sku: string;
  title: string | null;
  orders: number;
  revenue: number;
  margin: number | null;
}

export interface BiMarketplaceRow {
  marketplace: string;
  orders: number;
  revenue: number;
  margin: number;
  computable_count: number;
}

export interface BiSupplierRow {
  supplier_kind: string;
  orders: number;
  revenue: number;
  margin: number;
  computable_count: number;
}

export interface BiStatusRow {
  status: string;
  orders: number;
}

/** Le colonne economiche comuni a totale, canale, fornitore e giorno. Tutti
 *  gli importi sono SENZA IVA (Davide 27/10/2026). Le somme di costo e margine
 *  stanno solo sugli ordini calcolabili. */
export interface BiEconomia {
  orders: number;
  net_revenue: number;
  /** Incassato con IVA, solo sugli ordini che hanno il netto (backend dal 03/11). */
  gross_revenue?: number;
  /** Ordini senza fatturato netto (paese o IVA ignoti): fuori dal netto. */
  net_missing_count: number;
  certain_count: number;
  estimate_count: number;
  not_computable_count: number;
  net_revenue_computable: number;
  commission: number;
  shipping: number;
  packaging: number;
  product_cost: number;
  margin: number;
  loss_count: number;
  loss_amount: number;
}

export interface BiTotaleEconomia extends BiEconomia {
  replacements_count: number;
  replacements_cost_net: number;
}

export interface BiPeriodoPrima extends BiEconomia {
  date_from: string;
  date_to: string;
}

export interface BiMotivo {
  motivo: string;
  orders: number;
  not_computable_count: number;
}

export interface BiCanale extends BiEconomia {
  marketplace: string;
}

export interface BiFornitore extends BiEconomia {
  supplier_kind: string;
  /** Ordini il cui fornitore viene dalla fattura d'acquisto (backend dal 02/11). */
  from_invoice_count?: number;
  /** Ordini il cui fornitore viene dal catalogo PIM. */
  from_catalog_count?: number;
}

export interface BiGiorno extends BiEconomia {
  day: string;
}

export interface BiProdotto {
  sku: string;
  title: string | null;
  orders: number;
  quantity: number;
  net_revenue: number;
  margin: number | null;
  not_computable_count: number;
  estimate_count: number;
  /** Almeno un ordine con piu' articoli: netto e margine divisi per riga. */
  split: boolean;
}

export interface BiOrdinePerdita {
  order_id: number;
  reference: string;
  order_date: string | null;
  marketplace: string;
  net_revenue: number | null;
  margin: number;
  margin_percent: number | null;
  is_estimate: boolean;
  estimate_reasons: string[];
}

export interface BiPaese {
  country: string;
  /** Vero se nessun ordine diceva il paese: e' quello del negozio. */
  from_shop: boolean;
  orders: number;
  net_revenue: number;
  margin: number;
  not_computable_count: number;
}

export interface BiPostVendita {
  returned_orders: number;
  returned_net_revenue: number;
  cancelled_orders: number;
  refund_count: number;
  refund_net: number;
  refund_missing_count: number;
  supplier_refund_count: number;
  supplier_refund_net: number;
}

export interface BiProblemi {
  motivi: { categoria: string; threads: number; orders: number }[];
  invoice_requests: number;
  without_reason: number;
  pezzi: { supplier: string; part: string; pieces: number; cost: number; invoices: number }[];
  ricambi: { invoices: number; pieces: number; cost: number; invoices_without_order: number };
  prodotti: {
    sku: string;
    title: string | null;
    orders: number;
    problem_orders: number;
    returns: number;
    replacements: number;
    spare_parts: number;
    complaints: number;
  }[];
}

/** Resi e sostituzioni come costo, sugli ordini del periodo (02/11/2026). */
/** Le commissioni dei bonifici a Itamoby e Ityhome (0,50 € a bonifico, divise
 *  fra gli ordini che paga), dall'estratto conto caricato in Saldi. */
export interface BiCostiBonifici {
  total: string;
  orders: number;
  per_fornitore: { fornitore: string; orders: number; total: string }[];
  /** Commissioni di bonifici senza numero d'ordine, nel periodo: fuori dal totale. */
  senza_ordine: string;
}

export interface BiCostiPostVendita {
  orders: number;
  replacement_orders: number;
  /** Pezzi di ricambio dalle fatture Itamoby/Ityhome, senza IVA. */
  parts_cost: number;
  parts_invoices: number;
  /** Sostituzioni AllService scritte a mano, divise 1,22. */
  manual_cost: number;
  replacement_labels_cost: number;
  return_orders: number;
  /** Spedizione di andata dei resi: pagata e persa. */
  return_outbound_cost: number;
  return_labels_cost: number;
  return_no_shipping: number;
  return_supplier_refund: number;
  replacements_total: number;
  returns_total: number;
  total: number;
  /** Dal 03/11: rimborsi, merce persa nei resi, rimborsi del corriere. Assenti prima. */
  partial_refund_count?: number;
  /** Rimborsi parziali ai clienti, senza IVA, su ordini che restano vendite. */
  partial_refund_net?: number;
  sold_supplier_refund_count?: number;
  /** Note di credito dei fornitori su ordini venduti. */
  sold_supplier_refund?: number;
  /** Rimborsi parziali meno note fornitori su venduti. */
  adjustments_total?: number;
  return_goods_cost?: number;
  /** Prodotto e imballo pagati sui resi, meno il rimborso del fornitore (mai sotto 0). */
  return_goods_lost?: number;
  return_goods_lost_orders?: number;
  return_no_product_cost?: number;
  /** Resi senza «che fine fa la merce»: la merce si conta persa (03/11). */
  return_outcome_missing?: number;
  carrier_refund_count?: number;
  /** Rimborsi del corriere (Reclami). */
  carrier_refund?: number;
  /** Quanto hanno pagato i clienti per il ritorno, senza IVA (/1,22): abbassa il costo dei resi. */
  return_customer_paid?: number;
  /** Casi e costo per «Perché?» (resi + sostituzioni). ``non_indicata`` = pratiche vecchie. */
  by_cause?: BiCausaRiga[];
  /** Lo stesso, per causa e fornitore (campo fornitore della BI). */
  by_cause_supplier?: BiCausaFornitoreRiga[];
}

export interface BiCausaRiga {
  cause: string;
  cases: number;
  returns: number;
  replacements: number;
  cost: number;
}

export interface BiCausaFornitoreRiga {
  cause: string;
  supplier: string;
  cases: number;
  cost: number;
}

export interface BiDossierAnno {
  year: number;
  orders: number;
  replaced_orders: number;
  rate: number | null;
  parts_cost: number;
}

export interface BiDossierFornitore {
  supplier: string;
  orders: number;
  replaced_orders: number;
  rate: number | null;
  parts_cost: number;
  labels_cost: number;
  reinforced_packaging: number;
  free_parts: number;
  median_days: number | null;
  within_30_days: number | null;
  credit_notes: { reason: string; count: number; amount: number }[];
  years: BiDossierAnno[];
}

export interface BiCategoriaSpesa {
  categoria: string;
  nel_margine: boolean;
  totale: number;
  fornitori: { nome: string; totale: number; documenti: number }[];
}

export interface BiGruppoCanale {
  group: "sito" | "amazon" | "marketplace";
  orders: number;
  net_revenue: number;
  margin: number;
  ads: number;
  margin_after_ads: number;
  ads_per_order: number | null;
  roas: number | null;
}

export interface BiCostiStruttura {
  categorie: BiCategoriaSpesa[];
  struttura_totale: number;
  pubblicita: { sito: number; amazon: number };
  canali: BiGruppoCanale[];
  utile: {
    margin: number;
    post_vendita: number;
    /** Commissioni dei bonifici ai fornitori (dal 03/11; assente prima). */
    bonifici?: number;
    /** Rimborsi parziali meno note fornitori su venduti (dal 03/11). */
    rettifiche?: number;
    struttura: number;
    utile: number;
  };
}

export interface BiDossierTrend {
  fornitori: BiDossierFornitore[];
  componenti: {
    part: string;
    pieces: number;
    cost: number;
    orders: number;
    free: number;
    suppliers: string[];
  }[];
  modelli: {
    supplier: string;
    model: string;
    orders: number;
    replaced_orders: number;
    rate: number | null;
    cost: number;
    top_part: string | null;
  }[];
  ultimi: {
    supplier: string;
    date: string;
    part: string;
    cost: number;
    order_reference: string | null;
  }[];
}

export interface BiDettagli {
  economia: BiTotaleEconomia;
  precedente: BiPeriodoPrima | null;
  motivi: BiMotivo[];
  canali: BiCanale[];
  fornitori: BiFornitore[];
  giorni: BiGiorno[];
  prodotti: {
    sku_count: number;
    top_revenue: BiProdotto[];
    top_margin: BiProdotto[];
    worst_margin: BiProdotto[];
  };
  in_perdita: BiOrdinePerdita[];
  paesi: BiPaese[];
  post_vendita: BiPostVendita;
  /** Assente se il backend e' prima del 29/10. */
  problemi?: BiProblemi;
  /** Assenti se il backend e' prima del 02/11. */
  costi_post_vendita?: BiCostiPostVendita;
  dossier_trend?: BiDossierTrend;
  /** Spese dalle fatture, sito contro marketplace, utile vero (backend dal 02/11).
   *  ``null`` con un filtro di canale o fornitore: la struttura e' di tutti. */
  costi_struttura?: BiCostiStruttura | null;
  /** ``null`` finche' in Saldi non si carica un estratto conto. */
  costi_bonifici?: BiCostiBonifici | null;
  /** Fin dove arrivano i file scaricati a mano (backend dal 03/11). ``null`` = nulla caricato. */
  archivio?: { acquisto: string | null; vendita: string | null; banca: string | null };
}

export interface BiDashboardData {
  /** Null solo se il backend e' vecchio (prima della pagina BI 27/10). */
  dettagli: BiDettagli | null;
  totals: BiTotals;
  by_day: BiDayRow[];
  by_sku: BiSkuRow[];
  by_marketplace: BiMarketplaceRow[];
  by_supplier: BiSupplierRow[];
  by_status: BiStatusRow[];
  filters: {
    date_from: string | null;
    date_to: string | null;
    marketplace_id: string[];
    supplier_kind: string[];
    excluded_statuses: string[];
  };
}

export interface BiFilterState {
  dateFrom: string | null; // yyyy-mm-dd
  dateTo: string | null;
  /** Canali scelti; vuoto = tutti. */
  marketplaceIds: string[];
  /** Fornitori scelti; vuoto = tutti. */
  supplierKinds: string[];
}

export function fetchBiDashboard(filters: BiFilterState): Promise<BiDashboardData> {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  for (const id of filters.marketplaceIds) params.append("marketplace_id", id);
  for (const kind of filters.supplierKinds) params.append("supplier_kind", kind);
  const qs = params.toString();
  return apiGet<BiDashboardData>(`/api/economics/dashboard${qs ? `?${qs}` : ""}`);
}

// ── Costi logistici AllService (foglio Prefattura, migr. 0059) ──────────────

export interface AsCostMonth {
  month: string; // yyyy-mm-01
  entrata_qty: string | number;
  entrata_cost: string | number;
  reso_qty: string | number;
  reso_cost: string | number;
  picking_qty: string | number;
  picking_cost: string | number;
  etichette_qty: string | number;
  etichette_cost: string | number;
  spazio_mq: string | number;
  spazio_cost: string | number;
  pallet_qty: string | number;
  pallet_cost: string | number;
  extra_qty: string | number;
  extra_cost: string | number;
  canone_qty: string | number;
  canone_cost: string | number;
  varie_cost: string | number;
  total_cost: string | number; // Decimal Pydantic v2 -> stringa JSON
  synced_at: string;
}

/** Ultimi 12 mesi di costi logistici AS (aggiornati dal giro tracking orario). */
export function fetchAsCosts(): Promise<{ months: AsCostMonth[] }> {
  return apiGet<{ months: AsCostMonth[] }>("/api/economics/as-costs");
}

/** Ricalcola i margini pending (dopo un backfill o un cambio parametri AM). */
export function refreshMargins(): Promise<{ refreshed: number }> {
  return apiPost<{ refreshed: number }>("/api/economics/dashboard/refresh");
}

/** Accoda il backfill one-shot di TUTTO lo storico ordini Poleepo. */
export function startBackfillAll(): Promise<{ job_id: number; status: string }> {
  return apiPost<{ job_id: number; status: string }>("/api/orders/backfill-all");
}

export function useBiDashboard(filters: BiFilterState) {
  const [data, setData] = useState<BiDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ultima richiesta vince: filtri cambiati rapidamente non devono
  // sovrascrivere la risposta piu' recente con una piu' vecchia.
  const requestSeq = useRef(0);

  const refresh = useCallback(async () => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchBiDashboard(filters);
      if (seq === requestSeq.current) setData(result);
    } catch (err) {
      if (seq === requestSeq.current) {
        // prettifyApiError normalizza (strippa il prefisso [/api/...], mappa
        // gli errori di rete) coerente con le altre feature, invece di
        // mostrare err.message grezzo nel banner rosso.
        setError(prettifyApiError(err));
      }
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, isLoading, error, refresh };
}

// ── Helpers formato (it-IT, tabular) ─────────────────────────────────────────

export function euro(value: number | null | undefined, decimals = 0): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function pct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`;
}

/** yyyy-mm-dd dalla data LOCALE: mai toISOString().slice(), che converte in
 *  UTC e fa slittare il giorno indietro per i fusi a est di Greenwich. */
export function localIsoDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}
